import { useQuery } from '@apollo/client/react'
import React, { FC, useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { PileNode, PileNodeConnection } from '@src/generated/schema'
import { Pile } from '@src/graphql/Pile'
import debounce from 'lodash.debounce'
import { MobileSearchSelect } from '@src/components/Mobile/Elements/SearchSelect'
import { NetworkContext } from '@src/components/Mobile/Providers/Network'
import { MobileSelectProps } from '@src/components/Mobile/Elements/SearchSelect/SearchSelect'
import { getUniqueList } from '@src/helpers/Logic/Logic'

export interface MobileSortSelectProps extends Omit<MobileSelectProps, 'options'> {
  piles?: PileNode[]
}

export const MobileSortSelect: FC<MobileSortSelectProps> = ({ piles, ...props }) => {
  const { isOnline } = useContext(NetworkContext)
  const { t } = useTranslation()
  const { data, loading, fetchMore, refetch } = useQuery<{ pileSorts: PileNodeConnection }>(Pile.getPileSorts, {
    variables: {
      first: 20,
    },
    fetchPolicy: 'network-only',
  })
  const { pileSorts: { edges } = { edges: [] as any } } = data || {}

  const handleSearch = useCallback(
    debounce((value: string) => {
      refetch({
        first: 20,
        sort_Icontains: value,
      })
    }, 500),
    [],
  )

  const handlePopupScroll = () => {
    fetchMore({
      variables: {
        after: data?.pileSorts.pageInfo.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        const newEdges = fetchMoreResult.pileSorts.edges
        const pageInfo = fetchMoreResult.pileSorts.pageInfo

        return newEdges.length
          ? {
              pileSorts: {
                pageInfo,
                __typename: prev.pileSorts.__typename,
                edges: [...prev.pileSorts.edges, ...newEdges],
              },
            }
          : prev
      },
    })
  }

  return (
    <MobileSearchSelect
      {...props}
      label={t('desktop.pages.piles.filter.sort')}
      options={
        isOnline
          ? edges.map(({ node: { sort, id } }) => ({
              id,
              title: sort,
              value: sort,
            }))
          : getUniqueList(piles?.map((p) => p.sort)).map((sort) => ({
              id: sort,
              value: sort,
              title: sort,
            }))
      }
      loading={loading}
      onSearch={handleSearch}
      multi
      onLoadMore={handlePopupScroll}
      allowNoSelection
    />
  )
}
